import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Form, Input, Button } from "antd";
import { API_BASE_URL, API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useApi } from "../../hooks/useApi";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [isValidToken, setIsValidToken] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Initialize useApi hook at the top level
  const endpoint = token ? API_ENDPOINTS.RESET_PASSWORD(token) : "";
  const {
    execute,
    loading: apiLoading,
    error: apiError,
  } = useApi(endpoint, { method: "POST" }, false);

  // Extract token from URL
  useEffect(() => {
    // Get token from URL pathname
    const pathname = window.location.pathname;

    // Remove any trailing slashes and split
    const cleanPath = pathname.replace(/\/+$/, "");
    const pathParts = cleanPath.split("/");

    // Last part should be the token
    const extractedToken = pathParts[pathParts.length - 1];

    // Validate UUID format (Django expects UUID)
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (extractedToken && uuidRegex.test(extractedToken)) {
      setToken(extractedToken);
      setIsValidToken(true);
    } else {
      setIsValidToken(false);
      if (extractedToken && !["reset_password", ""].includes(extractedToken)) {
        Toast.error("Invalid reset token format. Token must be a valid UUID.");
      }
    }
  }, []);

  // ✅ FIXED: यह function अब values लेता है, event नहीं
  const handleSubmit = async (values) => {
    // values में password और confirmPassword होंगे
    const { password, confirmPassword } = values;

    if (!isValidToken || !token) {
      Toast.error("Invalid or missing reset token");
      return;
    }

    if (password !== confirmPassword) {
      Toast.error("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      Toast.error("Password must be at least 8 characters long");
      return;
    }

    // Check password strength (based on Django PASSWORD_REGEX)
    const strongPasswordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!strongPasswordRegex.test(password)) {
      Toast.error(
        "Password must contain at least 1 uppercase, 1 lowercase, 1 number, and 1 special character (@$!%*?&)",
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // Call API using useApi execute method
      const response = await execute({ body: { password } });

      // Handle Django response format
      if (
        response.status === true ||
        response.message?.toLowerCase().includes("success")
      ) {
        Toast.success("Password reset successfully! Redirecting to login...");

        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        // Show Django error message
        const errorMsg =
          response.error || response.message || "Failed to reset password";
        Toast.error(errorMsg);

        // If token is invalid/expired
        if (
          errorMsg.toLowerCase().includes("invalid") ||
          errorMsg.toLowerCase().includes("expired")
        ) {
          setTimeout(() => {
            Toast.info("Redirecting to forgot password page...");
            setTimeout(() => {
              navigate("/forgot-password");
            }, 1500);
          }, 2000);
        }
      }
    } catch (err) {
      console.error("Reset password error:", err);

      // Check for specific Django errors
      if (err.message.includes("400")) {
        Toast.error(
          "Bad request. Please check your password meets requirements.",
        );
      } else if (err.message.includes("404")) {
        Toast.error(
          "Reset endpoint not found. Please check API configuration.",
        );
      } else if (err.message.includes("500")) {
        Toast.error("Server error. Please try again later.");
      } else {
        Toast.error(err.message || "Network error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const loading = isSubmitting || apiLoading;

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoContainer}>
          <h2 style={styles.logoText}>Spelenderwijs Admin</h2>
        </div>

        <h2 style={styles.title}>Reset Password</h2>

        {isValidToken ? (
          <>
            <p style={styles.subtitle}>Enter your new password below</p>
            <div style={styles.passwordRules}>
              <p style={styles.ruleTitle}>Password Requirements:</p>
              <ul style={styles.ruleList}>
                <li style={styles.ruleItem}>✓ At least 8 characters long</li>
                <li style={styles.ruleItem}>✓ 1 uppercase letter (A-Z)</li>
                <li style={styles.ruleItem}>✓ 1 lowercase letter (a-z)</li>
                <li style={styles.ruleItem}>✓ 1 number (0-9)</li>
                <li style={styles.ruleItem}>✓ 1 special character (@$!%*?&)</li>
              </ul>
            </div>
          </>
        ) : (
          <div style={styles.warningBox}>
            <p style={styles.warningText}>⚠️ Invalid or expired reset link</p>
            <p style={styles.warningSubtext}>
              Please request a new password reset link from your email.
            </p>
            <Button
              type="primary"
              onClick={() => {
                Toast.info("Redirecting to forgot password page...");
                setTimeout(() => navigate("/forgot-password"), 500);
              }}
            >
              Request New Reset Link
            </Button>
          </div>
        )}

        {/* ✅ FIXED: Form में onFinish का सही उपयोग */}
        <Form layout="vertical" onFinish={handleSubmit} style={styles.form}>
          <Form.Item
            label="New Password"
            name="password"
            rules={[
              { required: true, message: "Please enter your new password" },
              { min: 8, message: "Password must be at least 8 characters" },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message:
                  "Uppercase, lowercase, number and special character (@$!%*?&) required",
              },
            ]}
            hasFeedback
          >
            <Input.Password
              placeholder="Enter strong password"
              disabled={!isValidToken || loading}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
            hasFeedback
          >
            <Input.Password
              placeholder="Confirm password"
              disabled={!isValidToken || loading}
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!isValidToken}
              block
              size="large"
            >
              Reset Password
            </Button>
          </Form.Item>

          <div style={styles.backLink}>
            <Button
              type="link"
              onClick={() => {
                Toast.info("Redirecting to login...");
                setTimeout(() => navigate("/login"), 500);
              }}
            >
              ← Back to Login
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

// Styles (same as before)
const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
    padding: "20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  card: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
    width: "100%",
    maxWidth: "500px",
  },
  logoContainer: {
    textAlign: "center",
    marginBottom: "30px",
  },
  logoText: {
    color: "#1890ff",
    fontSize: "28px",
    fontWeight: "700",
    margin: 0,
    letterSpacing: "0.5px",
  },
  title: {
    textAlign: "center",
    marginBottom: "20px",
    color: "#1f1f1f",
    fontSize: "26px",
    fontWeight: "600",
  },
  subtitle: {
    textAlign: "center",
    marginBottom: "20px",
    color: "#595959",
    fontSize: "15px",
    lineHeight: "1.5",
  },
  passwordRules: {
    backgroundColor: "#f6ffed",
    border: "1px solid #b7eb8f",
    borderRadius: "8px",
    padding: "18px",
    marginBottom: "25px",
  },
  ruleTitle: {
    fontWeight: "600",
    margin: "0 0 12px 0",
    color: "#389e0d",
    fontSize: "15px",
  },
  ruleList: {
    margin: "0",
    paddingLeft: "20px",
  },
  ruleItem: {
    color: "#595959",
    fontSize: "14px",
    marginBottom: "6px",
    lineHeight: "1.5",
  },
  warningBox: {
    backgroundColor: "#fff2f0",
    border: "1px solid #ffccc7",
    borderRadius: "8px",
    padding: "25px",
    marginBottom: "30px",
    textAlign: "center",
  },
  warningText: {
    color: "#ff4d4f",
    fontSize: "16px",
    marginBottom: "10px",
    fontWeight: "600",
  },
  warningSubtext: {
    color: "#ff7875",
    fontSize: "14px",
    marginBottom: "20px",
    lineHeight: "1.5",
  },
  secondaryButton: {
    padding: "12px 24px",
    backgroundColor: "#ff4d4f",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.2s",
  },
  form: {
    width: "100%",
  },
  backLink: {
    textAlign: "center",
    marginTop: "25px",
    paddingTop: "20px",
    borderTop: "1px solid #f0f0f0",
  },
};

// Add hover effects and spinner animation
const addHoverStyles = () => {
  if (typeof document === "undefined") return;

  const styleSheet = document.createElement("style");
  styleSheet.textContent = `
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    a:hover {
      color: #40a9ff !important;
      text-decoration: underline !important;
    }
    
    button[style*="background-color: #ff4d4f"]:hover {
      background-color: #ff7875 !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(255, 77, 79, 0.3) !important;
    }
  `;
  document.head.appendChild(styleSheet);
};

// Initialize hover styles
addHoverStyles();

export default ResetPassword;
